/**
 * AppBar — the standard shell bar, identical on every tool (h-12, the only
 * gradient in the system). Left: optional mobile hamburger + the fixed
 * SiteOperations.io wordmark + "|" divider — same on every page, so the
 * cross-tool switcher that follows always starts at the same X position
 * instead of drifting with a variable-width tool name. Right: a
 * module-owned slot (SiteSelector, bells, sign-out, …), the theme toggle,
 * and the Tool Hub link.
 *
 * The 3rem height participates in layout calcs (min-h-[calc(100vh-3rem)],
 * sticky top-12 sidebars) — modules that sync multiple calcs must change
 * them together.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { ThemeToggle } from '../theme/ThemeToggle';
import ToolNav from './ToolNav';

interface AppBarProps {
  /** Right-side slot — each module keeps its existing header contents here. */
  right?: ReactNode;
  /** When given, renders the mobile hamburger (lg:hidden) that opens the module's drawer. */
  onMenuClick?: () => void;
  /** Tool Hub / logo link target (defaults to '/'). */
  hubHref?: string;
}

export default function AppBar({ right, onMenuClick, hubHref }: AppBarProps) {
  return (
    <header className="sticky top-0 z-30 bg-gradient-to-r from-primary-600 to-primary-700">
      <div className="flex h-12 items-center gap-2 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-shrink-0 items-center gap-2">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Open navigation menu"
              className="lg:hidden -ml-2 inline-flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:h-9 sm:w-9 items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          <Link
            to={hubHref ?? '/'}
            aria-label="SiteOperations.io — Tool Hub"
            className="flex min-w-0 flex-shrink-0 items-center gap-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <img src="/pilllogo.svg" alt="" className="h-6 w-auto flex-shrink-0 brightness-0 invert" />
            <span className="hidden truncate text-[15px] font-semibold leading-none text-white sm:inline">
              SiteOperations.io
            </span>
          </Link>
          <span className="hidden text-white/40 sm:inline" aria-hidden="true">|</span>
        </div>
        {/* Cross-tool navigation — inline links (xl+) / "Tools" dropdown below. */}
        <ToolNav />
        <div className="flex flex-shrink-0 items-center gap-2">
          {right}
          <ThemeToggle variant="onColor" />
          <Link
            to={hubHref ?? '/'}
            className="hidden sm:inline-flex items-center rounded-lg px-2.5 py-1.5 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            Tool Hub
          </Link>
        </div>
      </div>
    </header>
  );
}
